import { useEffect, useRef, useState } from "react";
import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { Send, ShoppingBag } from "lucide-react";
import { JoinStageIcon } from "./icons/JoinStageIcon";
import { LeaveStageIcon } from "./icons/LeaveStageIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamChatOverlay } from "./LivestreamChatOverlay";
import { LivestreamRandomParticipantOverlay } from "./LivestreamRandomParticipantOverlay";
import { LivestreamShopOverlay } from "./LivestreamShopOverlay";
import { livestreamAudioSink } from "./livestreamAudioSink";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamPinnedComment } from "./useLivestreamPinnedComment";
import { useLivestreamRandomParticipant } from "./useLivestreamRandomParticipant";
import { useLivestreamStageFeeds } from "./useLivestreamStageFeeds";
import { useLivestreamRequestJoinVisibility } from "./useLivestreamRequestJoinVisibility";
import { useLivestreamStageRequest } from "./useLivestreamStageRequest";
import { useMeetingShopProducts } from "./useMeetingShopProducts";
import { MeetingViewerAuthGate } from "./MeetingViewerAuthGate";
import type { MeetingLeadContact, MeetingShopTipping } from "./meetingProductTypes";

export function LivestreamViewerView({
  meetingProductId,
  shopProductIds,
  shopTipping,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
}: {
  meetingProductId: string;
  shopProductIds: string[];
  shopTipping?: MeetingShopTipping;
  leadContact?: MeetingLeadContact;
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const chatInputRef = useRef<HTMLInputElement>(null);
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const [chatDraft, setChatDraft] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const showLogoutButton = showLogout && roomState !== "joined";
  const hasShop = shopProductIds.length > 0 || shopTipping?.enabled === true;
  const { products: shopProducts, loading: loadingShopProducts, load: loadShopProducts } =
    useMeetingShopProducts({ productIds: shopProductIds });
  const { messages, sendMessage } = useLivestreamChat({
    meeting,
    includeHistoricalMessages: true,
  });
  const { pinnedComment, requestPinnedCommentSync } = useLivestreamPinnedComment({
    meeting,
    isHost: false,
    messages,
  });
  const { requestJoinVisible, shopVisible, requestViewerConfigSync } =
    useLivestreamRequestJoinVisibility({
      meeting,
      isHost: false,
    });
  const { activeDraw, clearActiveDraw } = useLivestreamRandomParticipant({ meeting });
  const stageFeeds = useLivestreamStageFeeds({ meeting, includeSelf: false });
  const {
    requesting: requestingJoin,
    cancelling: cancellingJoinRequest,
    leaving: leavingStage,
    requestJoin,
    cancelRequestJoin,
    leaveStage,
    canRequestJoin,
    canCancelRequestJoin,
    canLeaveStage,
    requestJoinLabel,
    cancelRequestJoinLabel,
  } = useLivestreamStageRequest({ meeting });
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const hasChatDraft = Boolean(chatDraft.trim());
  const showLeaveStage = !hasChatDraft && canLeaveStage;
  const showCancelRequestJoin = !hasChatDraft && canCancelRequestJoin;
  const showRequestJoin =
    requestJoinVisible && !hasChatDraft && !hasStageScreenShare && canRequestJoin;
  const [audioUnlocked, setAudioUnlocked] = useState(
    () => livestreamAudioSink.isUnlocked(),
  );

  useEffect(() => {
    if (!meeting) return;
    void requestViewerConfigSync();
    void requestPinnedCommentSync();
  }, [meeting, requestPinnedCommentSync, requestViewerConfigSync]);

  useEffect(() => {
    if (livestreamAudioSink.isUnlocked()) {
      setAudioUnlocked(true);
      return;
    }

    const interactionEvents = ["touchend", "click", "keydown"] as const;

    const unlockAudio = () => {
      if (livestreamAudioSink.isUnlocked()) return;

      livestreamAudioSink.unlock();
      setAudioUnlocked(true);

      interactionEvents.forEach((eventType) => {
        window.removeEventListener(eventType, unlockAudio);
      });
    };

    interactionEvents.forEach((eventType) => {
      window.addEventListener(eventType, unlockAudio, { passive: true });
    });

    return () => {
      interactionEvents.forEach((eventType) => {
        window.removeEventListener(eventType, unlockAudio);
      });
    };
  }, []);

  const handleOpenShop = () => {
    setShopOpen(true);
    void loadShopProducts();
  };

  const handleSendChat = async () => {
    if (!chatDraft.trim() || sendingChat) return;
    setSendingChat(true);
    try {
      await sendMessage({ text: chatDraft });
      setChatDraft("");
    } finally {
      setSendingChat(false);
      chatInputRef.current?.focus();
    }
  };

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <MeetingViewerAuthGate>
    <div className="relative h-dvh w-screen overflow-hidden bg-black">
      {showLogoutButton ? (
        <MeetingLogoutButton
          showLogout={showLogoutButton}
          loggingOut={loggingOut}
          onLogout={onLogout}
        />
      ) : null}
      <LivestreamVideoGrid
        participants={stageFeeds}
        selfId={selfId}
        waitingLabel="Waiting for host…"
        hostPipDraggable
        showAudioUnmute
        audioUnlocked={audioUnlocked}
      />
      <LivestreamChatOverlay
        messages={messages}
        pinnedComment={pinnedComment}
        className={
          pinnedComment?.text
            ? "bottom-[calc(max(0.75rem,env(safe-area-inset-bottom))+2.5rem+8px)] sm:bottom-[calc(max(0.75rem,env(safe-area-inset-bottom))+2.5rem+8px)]"
            : undefined
        }
      />
      <LivestreamRandomParticipantOverlay draw={activeDraw} onComplete={clearActiveDraw} />
      <LivestreamShopOverlay
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        meetingProductId={meetingProductId}
        products={shopProducts}
        loading={loadingShopProducts}
        shopTipping={shopTipping}
        leadContact={leadContact}
        onSendChatMessage={sendMessage}
      />
      <form
        className="absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-3 right-3 z-50 flex items-center gap-2 sm:left-4 sm:right-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (hasChatDraft) {
            void handleSendChat();
          }
        }}
      >
        <Input
          ref={chatInputRef}
          value={chatDraft}
          onChange={(event) => setChatDraft(event.target.value)}
          placeholder="Say something…"
          disabled={sendingChat}
          className="h-10 flex-1 border-white/20 bg-black/55 text-white placeholder:text-white/50 backdrop-blur-sm"
          aria-label="Live chat message"
        />
        {hasChatDraft ? (
          <Button
            type="submit"
            size="icon"
            variant="outline"
            disabled={sendingChat}
            className="h-10 w-10 shrink-0 border-white/20 bg-black/55 text-white hover:bg-white/10 hover:text-white"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        ) : showLeaveStage ? (
          <Button
            type="button"
            variant="outline"
            disabled={leavingStage}
            onClick={() => {
              if (leavingStage) return;
              void leaveStage();
            }}
            className="h-10 w-11 shrink-0 flex-col gap-0 border-white/20 bg-black/55 px-1 py-1 text-white hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
            aria-label="Leave stage"
          >
            <LeaveStageIcon className="h-4 w-4 text-red-500" />
            <span className="text-[9px] leading-none text-white/70">Leave</span>
          </Button>
        ) : showCancelRequestJoin ? (
          <Button
            type="button"
            variant="outline"
            disabled={cancellingJoinRequest}
            onClick={() => {
              if (cancellingJoinRequest) return;
              void cancelRequestJoin();
            }}
            className="h-10 w-11 shrink-0 flex-col gap-0 border-white/20 bg-black/55 px-1 py-1 text-white hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
            aria-label={cancelRequestJoinLabel}
          >
            <LeaveStageIcon className="h-4 w-4 text-red-500" />
            <span className="text-[9px] leading-none text-white/70">Cancel</span>
          </Button>
        ) : null}
        {showRequestJoin ? (
          <Button
            type="button"
            variant="outline"
            disabled={requestingJoin}
            onClick={() => {
              if (requestingJoin) return;
              void requestJoin();
            }}
            className="h-10 w-11 shrink-0 flex-col gap-0 border-white/20 bg-black/55 px-1 py-1 text-white hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
            aria-label={requestJoinLabel}
          >
            <JoinStageIcon className="h-4 w-4" />
            <span className="text-[9px] leading-none text-white/70">{requestJoinLabel}</span>
          </Button>
        ) : null}
        {hasShop && shopVisible ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleOpenShop}
            className="h-10 w-11 shrink-0 flex-col gap-0 border-white/20 bg-black/55 px-1 py-1 text-white hover:bg-white/10 hover:text-white"
            aria-label="Shop"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="text-[9px] leading-none text-white/70">Shop</span>
          </Button>
        ) : null}
      </form>
    </div>
    </MeetingViewerAuthGate>
  );
}
